<template>
  <div>
    <!-- Backdrop -->
    <div 
      v-if="isOpen" 
      class="message-backdrop" 
      @click="closePanel"
    ></div>

    <!-- Message Panel -->
    <div 
      :class="['message-panel', isOpen ? 'message-panel-open' : 'message-panel-closed']"
    >
      <!-- Header -->
      <div class="message-header">
        <h2 class="message-title">Messages</h2>
        <button class="btn-close-circle" @click="closePanel">×</button>
      </div>

      <!-- Body -->
      <div class="message-content horizontal-layout">

        <!-- Contact List -->
        <div class="contact-list-panel" :style="{ width: contactListWidth + 'px' }">
          <ul class="contact-list">
            <li 
              v-for="contact in sortedContacts" 
              :key="contact.id" 
              @click="openPanel(contact)"
              :class="[
                'contact-item', 
                activeChat && activeChat.id === contact.id ? 'active' : '',
                contact.isAdmin ? 'admin-contact' : ''
              ]"
            >
              <div v-if="contact.isAdmin" class="admin-badge-icon">
                <img src="../../assets/notif_icon.png" alt="admin" class="admin-icon-img" />
              </div>

              <img v-else :src="contact.avatar" class="avatar" />

              <div class="contact-info">
                <p class="name">
                  {{ contact.name }}
                  <span v-if="contact.isAdmin" class="admin-label">Admin</span>
                </p>
                <p class="preview">{{ contact.lastMessage }}</p>
                <span 
                  v-if="contact.unreadCount && contact.unreadCount > 0" 
                  class="notification-badge"
                >
                  {{ contact.unreadCount }}
                </span>
              </div>
            </li>
          </ul>
        </div>

        <!-- Divider -->
        <div 
          class="divider" 
          @mousedown="startDrag"
        ></div>

        <!-- Chat Panel -->
        <div class="chat-panel" v-if="activeChat">

          <!-- Chat Header -->
          <div :class="['chat-header', activeChat.isAdmin ? 'admin-chat-header' : '']">
            <div class="chat-header-info">
              <h3>
                {{ activeChat.name }}
                <span v-if="activeChat.isAdmin" class="admin-header-label">Admin Announcements</span>
              </h3>
              <p v-if="activeChat.isAdmin" class="admin-subtitle">
                Official announcements from the platform
              </p>
              <div v-else class="conversation-start-time">
                {{ activeChat.lastMessageAt ? formatDate(activeChat.lastMessageAt) : '' }}
              </div>
            </div>
          </div>

            <div v-if="chatPending" class="accept-chat-modal">
              <p>This chat has not been accepted yet. Do you want to join?</p>
              <div class="modal-actions">
                <button @click="acceptChat">Accept</button>
              </div>
            </div>
          <div v-if="!chatPending" class="chat-box">
            <div 
              v-for="(msg, index) in messages" 
              :key="msg.id"
            >
              <div v-if="shouldShowDate(index)" class="date-separator">
                {{ formatDate(msg.timestamp) }}
              </div>

              <div :class="['msg-row', msg.fromMe ? 'from-me-row' : 'from-them-row']">

                <img 
                  v-if="!msg.fromMe" 
                  :src="activeChat.avatar || defaultAvatar"
                  class="msg-avatar" 
                />

                <div :class="['msg', msg.fromMe ? 'from-me' : 'from-them']">
                  <p class="msg-text">{{ msg.text }}</p>
                  <span class="msg-time">{{ formatTime(msg.timestamp) }}</span>
                </div>

                <img 
                  v-if="msg.fromMe" 
                  :src="myAvatar"
                  class="msg-avatar" 
                />
              </div>
            </div>

            <!-- Moderation modal (from old component) -->
            <b-modal id="send-unavailable" centered title="Message flagged">
              <p class="confirm-modal-text">
                Your message has been flagged by our automatic moderation.
                It has not been sent.
              </p>
              <template #modal-footer>
                <div class="float-right">
                  <b-button class="close-button" @click="closeUnavailableModal">
                    Close
                  </b-button>
                </div>
              </template>
            </b-modal>
          </div>

          <div v-if="!chatPending && !activeChat.isAdmin" class="input-area">
            <input 
              v-model="newMessage" 
              placeholder="Type a message..." 
              @keyup.enter="sendMessageToFirestore" 
            />
            <button @click="sendMessageToFirestore">Send</button>
          </div>

        </div> <!-- chat-panel -->

      </div> <!-- message-content -->

    </div> <!-- message-panel -->
  </div>
</template>


<script>
import Chat from '../../Firestore/Chat.js'
import Moderation from '../../Firestore/Moderation.js'

export default {
  name: 'MessagePanel',
  
  props: {
    contacts: { 
      type: Array, 
      required: true 
    },
    userType: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      chatPending: false,
      isOpen: false,
      activeChat: null,
      messages: [],
      newMessage: '',
      currentConversation: null,
      currentContact: null,
      isLoadingMessages: false,
      messageUnsubscribe: null,
      contactListWidth: 280, // Default width
      isDragging: false,
      minWidth: 200,
      maxWidth: 450
    }
  },

  computed: {
    defaultAvatar() {
      return require('../../assets/avator_example.jpg');
    },
    myAvatar() {
      return window.localStorage.getItem('userAvatar') || this.defaultAvatar;
    },
    sortedContacts() {
      return [...this.contacts].sort((a, b) => {
        if (a.isAdmin && !b.isAdmin) return -1;
        if (!a.isAdmin && b.isAdmin) return 1;
        return 0;
      });
    }
  },

  watch: {
    activeChat(newVal) {
      if (newVal && newVal.acceptedStatus === "pending" && this.userType === 0) {
        this.chatPending = true;
      } else {
        this.chatPending = false;
      }
    },
    isOpen(newVal) {
      if (!newVal) {
        if (this.messageUnsubscribe) {
          this.messageUnsubscribe();
          this.messageUnsubscribe = null;
        }
      }
    }
  },

  methods: {
    async acceptChat() {
      const result = await Chat.acceptConversation(this.currentConversation)
      if (result.success) {
        this.activeChat.acceptedStatus = "accepted";
        this.chatPending = false;
      } else {
        console.log("Error accepting chat:", error);
      }
    },
    startDrag(event) {
      this.isDragging = true;
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      event.preventDefault();
    },

    onDrag(event) {
      if (!this.isDragging) return;
      
      const panel = this.$el.querySelector('.message-panel');
      const panelRect = panel.getBoundingClientRect();
      const newWidth = event.clientX - panelRect.left;
      
      if (newWidth >= this.minWidth && newWidth <= this.maxWidth) {
        this.contactListWidth = newWidth;
      }
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    openPanel(contact) {
      console.log("Switching to contact:", contact);

      if (!contact || !contact.id) return;

      if (this.messageUnsubscribe) {
        this.messageUnsubscribe();
        this.messageUnsubscribe = null;
      }

      this.activeChat = contact;
      this.currentContact = contact;
      this.currentConversation = contact.conversationId || null;
      this.isOpen = true;
      this.messages = [];

      contact.unreadCount = 0;

      if (this.currentConversation) {
        this.loadMessagesFromFirestore();
      } else if (contact.messages) {
        this.messages = [...contact.messages];
      }

      this.$nextTick(() => {
        const chatBox = document.querySelector('.chat-box');
        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
      });
    },

    closePanel() {
      if (this.messageUnsubscribe) {
        this.messageUnsubscribe();
        this.messageUnsubscribe = null;
      }
      
      this.isOpen = false;
      this.activeChat = null;
      this.messages = [];
      this.newMessage = '';
      this.currentConversation = null;
      this.currentContact = null;
      
      document.body.style.overflow = '';
    },

    setConversation(conversationId, contact) {
      this.currentConversation = conversationId;
      this.currentContact = contact;
      this.activeChat = contact;
      this.openPanel(contact);
    },

    openChatWithContact(contact) {
      console.log('MessagePanel: Opening chat with contact:', contact);
      
      if (!contact || !contact.id || !contact.conversationId) {
        console.error('Invalid contact object:', contact);
        alert('Invalid contact data. Please try again.');
        return;
      }
      
      this.activeChat = contact;
      this.currentConversation = contact.conversationId;
      this.currentContact = contact;
      this.isOpen = true;
      
      const existingContactIndex = this.contacts.findIndex(c => c.id === contact.id);
      if (existingContactIndex >= 0) {
        this.$set(this.contacts, existingContactIndex, contact);
      } else {
        this.contacts.push(contact);
      }
      
      this.messages = [];
      this.loadMessagesFromFirestore();
      
      contact.unreadCount = 0;
      document.body.style.overflow = 'hidden';
      
      console.log('Chat opened successfully');
    },

    loadMessagesFromFirestore() {
      if (!this.currentConversation) {
        console.error('No conversation ID set');
        return;
      }
      
      console.log('Loading messages for conversation:', this.currentConversation);
      
      if (this.messageUnsubscribe) {
        this.messageUnsubscribe();
        this.messageUnsubscribe = null;
      }
      
      this.isLoadingMessages = true;
      const userId = window.localStorage.getItem('userId');
      
      Chat.getMessages(
        this.currentConversation,
        50,
        (result) => {
          if (result.success) {
            console.log('Messages received:', result.data.length);
            
            this.messages = result.data.map(msg => {
              let timestamp = msg.timestamp;
              if (timestamp && typeof timestamp.toDate === 'function') {
                timestamp = timestamp.toDate();
              }
              
              return {
                id: msg.messageId,
                text: msg.messageText,
                fromMe: msg.senderId === userId,
                timestamp: timestamp,
                read: msg.read
              };
            });
            
            this.$nextTick(() => {
              const chatBox = document.querySelector('.chat-box');
              if (chatBox) {
                chatBox.scrollTop = chatBox.scrollHeight;
              }
            });
            
            this.isLoadingMessages = false;
          } else {
            console.error('Error loading messages:', result.error);
            this.isLoadingMessages = false;
          }
        }
      ).then(result => {
        if (result.success && result.unsubscribe) {
          this.messageUnsubscribe = result.unsubscribe;
          console.log('Real-time listener attached');
        }
      }).catch(err => {
        console.error('Error setting up message listener:', err);
        alert('Failed to load messages: ' + (err.error || err.message || 'Unknown error'));
        this.isLoadingMessages = false;
      });
    },

    async sendMessageToFirestore() {
      if (!this.newMessage.trim()) return;
      
      if (!this.currentConversation) {
        this.sendMessage();
        return;
      }
      try{
        const result = await Moderation.moderateText(this.newMessage)
        if (!result.success){
          this.$bvModal.show("send-unavailable");
          return;
        }
      } catch (err) {
        console.log("Error moderating message! (this is a big problem)", err)
      }
      
      const userId = window.localStorage.getItem('userId');
      
      Chat.sendMessage(
        this.currentConversation,
        userId,
        this.newMessage,
        'text'
      ).then(result => {
        if (result.success) {
          this.newMessage = '';
        } else {
          console.error('Error sending message:', result.error);
        }
      }).catch(err => {
        console.error('Error in sendMessage:', err);
      });
    },

    closeUnavailableModal() {
      this.$bvModal.hide('send-unavailable');
    },

    sendMessage() {
      if (!this.newMessage.trim() || !this.activeChat) return;

      const newMsg = {
        id: Date.now(),
        text: this.newMessage,
        fromMe: true,
        timestamp: new Date()
      };

      this.messages.push(newMsg);
      if (this.activeChat.messages) {
        this.activeChat.messages.push(newMsg);
      }
      this.activeChat.lastMessage = newMsg.text;
      this.activeChat.lastMessageAt = newMsg.timestamp;

      this.newMessage = '';

      this.$nextTick(() => {
        const chatBox = document.querySelector('.chat-box');
        if (chatBox) chatBox.scrollTop = chatBox.scrollHeight;
      });
    },

formatDate(date) {
  if (!date) return '';

  // ✅ Firestore Timestamp -> JS Date
  if (typeof date.toDate === "function") {
    date = date.toDate();
  }

  // ✅ string / number -> Date
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  // ✅ Invalid Date 防护
  if (isNaN(date)) return '';

  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
},
shouldShowDate(index) {
  if (index === 0) return true;

  const curr = this.messages[index].timestamp;
  const prev = this.messages[index - 1].timestamp;

  if (!curr || !prev) return false;

  const d1 = new Date(curr);
  const d2 = new Date(prev);

  return d1.toDateString() !== d2.toDateString();
},

formatTime(date) {
  if (!date) return '';

  if (typeof date.toDate === "function") {
    date = date.toDate();
  }

  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  if (isNaN(date)) return '';

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
  },

  beforeDestroy() {
    if (this.messageUnsubscribe) {
      console.log('Cleaning up message listener');
      this.messageUnsubscribe();
      this.messageUnsubscribe = null;
    }
    
    // Clean up drag event listeners
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.stopDrag);
    
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
.message-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.horizontal-layout {
  display: flex;
  height: calc(100% - 50px);
}

.message-content.horizontal-layout {
  display: flex;
  flex-direction: row;
  height: calc(100% - 50px);
}

.message-content {
  flex: 1;
  overflow: hidden;
  padding: 0;
  display: flex;
  height: 100%;
}

.contact-list-panel {
  min-width: 200px;
  max-width: 450px;
  width: 280px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ddd;
  background: white;
  overflow: hidden;
}

.contact-list {
  flex: 1;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Draggable Divider */
.divider {
  width: 4px;
  background: #ddd;
  cursor: col-resize;
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
}

.divider:hover {
  background: #FF7222;
}

.divider:active {
  background: #e55a0f;
}

.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0; /* Important for flex child to shrink properly */
}

.chat-box {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end; /* Anchor messages to the bottom */
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #f94848ff;
  color: white;
  font-size: 10px;
  font-weight: bold;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-panel {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 900px;
  background: white;
  z-index: 1001;
  transform: translateX(100%);
  transition: all 0.4s ease;
  display: flex;
  flex-direction: column;
}

.message-panel-open {
  transform: translateX(0);
}

.message-header {
  /* background: #5226DF; */
  background: #ff7a1f;
  color: white;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.message-title {
  margin: 0;
  font-size: 1.5rem;
}



.conversation-start-time {
  text-align: center;
  color: #888;
  font-size: 0.9em;
  margin: 8px 0;
}

/* Admin Contact Styling */
.contact-item.admin-contact {
  background: linear-gradient(135deg, rgba(255, 162, 108, 0.15), rgba(255, 114, 34, 0.15));
  border-left: 4px solid #FF7222;
  box-shadow: 0 2px 4px rgba(255, 114, 34, 0.1);
}

.contact-item.admin-contact:hover {
  background: linear-gradient(135deg, rgba(255, 162, 108, 0.25), rgba(255, 114, 34, 0.25));
}

.contact-item.admin-contact.active {
  background: linear-gradient(135deg, rgba(255, 162, 108, 0.3), rgba(255, 114, 34, 0.3));
  box-shadow: inset 4px 0 0 0 #FF7222;
}

.admin-badge-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffa26c, #FF7222);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.admin-icon-img {
  width: 28px;
  height: 28px;
  filter: brightness(0) invert(1);
}
.date-separator {
  text-align: center;
  color: #888;
  font-size: 0.8em;
  background: #f3f3f3;
  padding: 4px 10px;
  margin: 10px auto;
  border-radius: 12px;
  width: fit-content;
}
.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s;
}

.contact-item.active {
  background-color: #f0f0f0;
  box-shadow: inset 3px 0 0 0 #e55a0f;
}

.contact-item:hover {
  background: #f8f8f8;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;
}

.contact-info {
  flex: 1;
  position: relative;
  min-width: 0; /* Important for text truncation */
}

.name {
  font-weight: bold;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  color: #333; /* Keep text black */
}

/* Keep contact name black even when active */
.contact-item.active .name {
  color: #333;
}

.admin-label {
  display: inline-block;
  font-size: 0.7rem;
  background: linear-gradient(135deg, #ffa26c, #FF7222);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.preview {
  color: gray;
  font-size: 1rem;
  margin: 4px 0 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: center; /* Center the content */
  gap: 10px;
  margin-bottom: 10px;
  padding: 16px 12px; /* Slightly more padding for better centering */
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.chat-header.admin-chat-header {
  background: linear-gradient(135deg, rgba(255, 162, 108, 0.15), rgba(255, 114, 34, 0.15));
  border-left: 4px solid #FF7222;
}

.chat-header-info {
  text-align: center; /* Center the text */
  min-width: 0;
}

.chat-header-info h3 {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center; /* Center the name and badge */
  gap: 8px;
  font-size: 1.25rem;
  color: #333; /* Keep header text black */
}

.admin-header-label {
  display: inline-block;
  font-size: 0.75rem;
  background: linear-gradient(135deg, #ffa26c, #FF7222);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.admin-subtitle {
  font-size: 0.85rem;
  color: #666;
  margin: 4px 0 0 0;
}

.msg-row {
  display: flex;
  align-items: flex-end;
  margin-bottom: 8px;
}

.from-me-row {
  justify-content: flex-end;
}

.from-them-row {
  justify-content: flex-start;
}

.msg-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin: 0 6px;
  flex-shrink: 0;
}

.msg {
  padding: 8px 12px;
  border-radius: 12px;
  max-width: 70%;
  position: relative;
  display: flex;
  flex-direction: column;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.msg-text {
  margin: 0;
  font-size: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: left;
}

.msg-time {
  font-size: 0.7rem;
  color: gray;
  align-self: flex-end;
  margin-top: 2px;
  white-space: nowrap;
}

.from-me {
  background: #c6edf8ff;
  text-align: right;
}

.from-them {
  background: #fde4acff;
  text-align: left;
}

.from-admin {
  background: linear-gradient(135deg, rgba(255, 162, 108, 0.3), rgba(255, 114, 34, 0.3));
  border: 1px solid rgba(255, 114, 34, 0.3);
}

.input-area {
  display: flex;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #ddd;
  background: white;
  flex-shrink: 0;
}

.input-area input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  outline: none;
  font-size: 1rem;
}

.input-area input:focus {
  border-color: #FF7222;
  box-shadow: 0 0 3px rgba(255, 114, 34, 0.3);
}

.input-area button {
  padding: 8px 16px;
  border: none;
  background: #FF7222;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;
}

.input-area button:hover {
  background: #e55a0f;
}

.accept-chat-modal {
  position: absolute;        
  top: 50%;
  left: 60%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  z-index: 1000;
  width: 300px;
  text-align: center;
  font-family: inherit;
}

.accept-chat-modal .modal-text {
  margin: 0 0 15px;
  font-size: 0.95rem;
  color: #333;
}

.accept-chat-modal .modal-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.accept-chat-modal .modal-actions button {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background-color: #FF7222;
  color: white;
  cursor: pointer;
  font-size: 0.9rem;
}

.accept-chat-modal .modal-actions button:hover {
  background-color: #e55a0f;
}

/* Scrollbar Styling */
/* Scrollbar styles inherited from global App.vue */
</style>


// WEBPACK FOOTER //
// src/components/messagebox/developer-messagepanel.vue